import React, { useState, FormEvent } from 'react';
import {
  collection,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import {
  signInWithEmailAndPassword,
  User,
} from 'firebase/auth';

import { firestore } from './providers/firebase';
import { auth } from './providers/firebase';

interface ConferenceFormProps {}

const App: React.FC<ConferenceFormProps> = () => {
  const [eventName, setEventName] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [dateStart, setDateStart] = useState<string>('');
  const [dateEnd, setDateEnd] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string>('');
  const [ticketsLink, setTicketsLink] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [user, setUser] = useState<User | null>(null);

  const handleSignInSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setUser(userCredential.user);
    }
    catch (error: any) {
      setErrorMessage(error.message);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      if (!user) {
        throw new Error('User not authenticated.');
      }
      const startDateTimestamp = Timestamp.fromDate(new Date(dateStart));
      const endDateTimestamp = (dateEnd) ? Timestamp.fromDate(new Date(dateEnd)) : null;

      await addDoc(collection(firestore, 'talks'), {
        eventName,
        location,
        dateStart: startDateTimestamp,
        dateEnd: endDateTimestamp,
        countryCode,
        ticketsLink
      });

      setSuccessMessage('Conference data saved successfully!');
      setEventName('');
      setLocation('');
      setDateStart('');
      setDateEnd(null);
      setCountryCode('');
      setTicketsLink('');
    } catch (error: any) {
      console.error('Error adding document: ', error);
      setErrorMessage(error.message || 'Failed to save conference data.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
        <h2>Sign In</h2>
        <form onSubmit={handleSignInSignUp}>
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="password">Password:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Loading...' : 'Sign In'}
          </button>
        </form>
        {errorMessage && (
          <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <h2>Add Conference Details</h2>
      <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: '10px' }}>
          <label htmlFor="eventName">Event Name:</label>
          <input
            type="text"
            id="eventName"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="location">Location:</label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="dateStart">Start Date:</label>
          <input
            type="date"
            id="dateStart"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="dateEnd">End Date:</label>
          <input
            type="date"
            id="dateEnd"
            value={dateEnd ?? ''}
            onChange={(e) => setDateEnd(e.target.value)}
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="countryCode">Country Code:</label>
          <input
            type="text"
            id="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="ticketsLink">Tickets Link:</label>
          <input
            type="url"
            id="ticketsLink"
            value={ticketsLink}
            onChange={(e) => setTicketsLink(e.target.value)}
            required
            style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Saving...' : 'Save Conference'}
        </button>
      </form>
      {successMessage && (
        <div style={{ color: 'green', marginTop: '10px' }}>{successMessage}</div>
      )}
      {errorMessage && (
        <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>
      )}
    </div>
  );
};

export default App;
